<template>
  <!-- Sticky, dismissable beta-feedback bar. Self-manages its own visibility
       from localStorage: shows on load until the learner has dismissed it 3
       times, or has sent feedback once (then it never returns). -->
  <transition
    enter-active-class="transition duration-300 ease-out"
    enter-from-class="translate-y-full opacity-0"
    enter-to-class="translate-y-0 opacity-100"
    leave-active-class="transition duration-200 ease-in"
    leave-from-class="translate-y-0 opacity-100"
    leave-to-class="translate-y-full opacity-0"
  >
    <div
      v-if="visible"
      class="feedback-footer fixed inset-x-0 bottom-0 z-40 px-3 pb-3 sm:px-4 sm:pb-4 pointer-events-none"
    >
      <div
        class="pointer-events-auto mx-auto max-w-3xl rounded-2xl border border-emerald-200 dark:border-emerald-800 bg-white/95 dark:bg-gray-800/95 backdrop-blur shadow-lg ring-1 ring-black/5 px-3 sm:px-4 py-3 flex items-center gap-2 sm:gap-3"
      >
        <span class="text-xl shrink-0" aria-hidden="true">🌱</span>
        <p class="min-w-0 flex-1 text-sm text-gray-700 dark:text-gray-200 leading-snug">
          <span class="font-semibold text-gray-900 dark:text-gray-100">We reshaped the course.</span>{{ ' ' }}
          <span class="hidden sm:inline">How's the new order and lessons feeling?</span><span class="sm:hidden">How's it feeling?</span>
        </p>
        <button
          @click="open"
          class="shrink-0 px-3 sm:px-3.5 py-2 rounded-xl text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700"
        >
          Share feedback
        </button>
        <button
          @click="dismiss"
          aria-label="Dismiss"
          class="shrink-0 -mr-1 h-8 w-8 grid place-items-center rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 text-lg leading-none"
        >
          &times;
        </button>
      </div>
    </div>
  </transition>

  <FeedbackModal v-if="modalOpen" @close="modalOpen = false" @submitted="onSubmitted" />
</template>

<script setup>
import { ref, onMounted } from 'vue'
import FeedbackModal from './FeedbackModal.vue'
import { track } from '../../utils/analytics'

// Re-appears until dismissed this many times, then stays gone.
const MAX_DISMISSALS = 3
const DISMISS_KEY = 'ss_feedback_dismiss_count'
const DONE_KEY = 'ss_feedback_done'

const visible = ref(false)
const modalOpen = ref(false)

function readInt(key) {
  try { return parseInt(localStorage.getItem(key) || '0', 10) || 0 } catch (e) { return 0 }
}
function done() {
  try { return localStorage.getItem(DONE_KEY) === '1' } catch (e) { return false }
}

onMounted(() => {
  if (done()) return
  if (readInt(DISMISS_KEY) >= MAX_DISMISSALS) return
  visible.value = true
  track('feedback_footer_shown', { dismissals: readInt(DISMISS_KEY) })
})

function open() {
  modalOpen.value = true
  track('feedback_footer_opened')
}

function dismiss() {
  const next = readInt(DISMISS_KEY) + 1
  try { localStorage.setItem(DISMISS_KEY, String(next)) } catch (e) { /* ignore */ }
  visible.value = false
  track('feedback_footer_dismissed', { dismissals: next })
}

function onSubmitted() {
  // Sent feedback once — retire the footer for good on this device.
  try { localStorage.setItem(DONE_KEY, '1') } catch (e) { /* ignore */ }
  visible.value = false
  // Modal keeps showing its own thank-you view until the learner closes it.
}
</script>
